import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import TestimonialSection from '@/components/TestimonialSection';
import ReferralSection from '@/components/ReferralSection';
import { 
  Image, 
  Wand2, 
  Sparkles, 
  Camera, 
  Palette, 
  Workflow,
  ArrowRight,
  Clock,
  Zap
} from 'lucide-react';

const Dashboard = () => {
  const { user, api } = useAuth();
  const { t } = useLanguage();
  const [creations, setCreations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const creationsRes = await api.get('/creations?limit=6');
      setCreations(creationsRes.data.creations || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const tools = [
    { 
      path: '/dashboard/image-to-prompt', 
      icon: <Image className="w-6 h-6" />, 
      title: t('tools.imageToPrompt'),
      description: t('tools.imageToPromptDesc')
    },
    { 
      path: '/dashboard/prompt-enhancer', 
      icon: <Wand2 className="w-6 h-6" />, 
      title: t('tools.promptEnhancer'),
      description: t('tools.promptEnhancerDesc')
    },
    { 
      path: '/dashboard/image-generator', 
      icon: <Sparkles className="w-6 h-6" />, 
      title: t('tools.imageGenerator'),
      description: t('tools.imageGeneratorDesc')
    },
    { 
      path: '/dashboard/mockup-generator', 
      icon: <Camera className="w-6 h-6" />, 
      title: t('tools.mockupGenerator'),
      description: t('tools.mockupGeneratorDesc')
    },
    { 
      path: '/dashboard/background-generator', 
      icon: <Palette className="w-6 h-6" />, 
      title: t('tools.backgroundGenerator'),
      description: t('tools.backgroundGeneratorDesc')
    },
    { 
      path: '/dashboard/workflow', 
      icon: <Workflow className="w-6 h-6" />, 
      title: t('tools.workflow'),
      description: t('tools.workflowDesc')
    },
  ];

  const getTypeLabel = (type) => {
    const labels = {
      'image_to_prompt': 'Prompt',
      'prompt_enhance': 'Enhanced',
      'image_generate': 'Image',
      'mockup_generate': 'Mockup',
      'background_generate': 'Background'
    };
    return labels[type] || type;
  };

  return (
    <div className="space-y-8 fade-in" data-testid="dashboard">
      {/* Welcome Section */}
      <div>
        <h1 
          className="text-2xl sm:text-3xl font-bold mb-2"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          {t('dashboard.welcome')}, {user?.first_name || user?.name?.split(' ')[0] || user?.email?.split('@')[0]}
        </h1>
        <p className="text-muted-foreground">
          {t('dashboard.whatCreate')}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card data-testid="creations-count-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('dashboard.creations')}</p>
                <p className="text-2xl font-bold">{creations.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="plan-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center">
                <Zap className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Plan</p>
                <p className="text-2xl font-bold capitalize">{user?.plan || 'Free'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 
          className="text-lg font-semibold mb-4"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          {t('dashboard.quickActions')}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {tools.map((tool, index) => (
            <motion.div
              key={tool.path}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Link to={tool.path}>
                <Card className="tool-card h-full" data-testid={`tool-card-${tool.title.toLowerCase().replace(/\s+/g, '-')}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center">
                        {tool.icon}
                      </div>
                    </div>
                    <h3 className="font-semibold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {tool.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {tool.description}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Referral Section - Invite & Earn */}
      <ReferralSection />

      {/* Recent Creations */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 
            className="text-lg font-semibold"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {t('dashboard.recentCreations')}
          </h2>
          {creations.length > 0 && (
            <Link to="/dashboard/settings" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32 rounded-xl" />
            ))}
          </div>
        ) : creations.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 bg-muted rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('dashboard.noCreations')}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Start by using one of the tools above
              </p>
              <Link to="/dashboard/image-generator">
                <Button data-testid="start-creating-btn">{t('dashboard.startCreating')}</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {creations.map((creation) => (
              <Card key={creation.id} className="overflow-hidden" data-testid={`creation-${creation.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium bg-accent px-2 py-1 rounded">
                      {getTypeLabel(creation.type)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(creation.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground truncate">
                    {creation.output_data?.enhanced || 
                     creation.output_data?.prompts?.general?.substring(0, 50) || 
                     creation.input_data?.prompt?.substring(0, 50) ||
                     'Creation'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {creation.credits_used} credits used
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Upgrade CTA for free users */}
      {user?.plan === 'free' && (
        <Card className="bg-muted/50 border-dashed" data-testid="upgrade-cta">
          <CardContent className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Unlock more with Growth
              </h3>
              <p className="text-sm text-muted-foreground">
                Get 500 credits/month, HD outputs, and priority processing
              </p>
            </div>
            <Link to="/pricing">
              <Button data-testid="upgrade-btn">
                Upgrade Now <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Testimonial Section */}
      <TestimonialSection />
    </div>
  );
};

export default Dashboard;
